import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, Table, Form, Row, Col, Spinner, Alert, Button } from 'react-bootstrap';
import * as XLSX from 'xlsx';

// 📅 Helper pour obtenir les dates du mois en cours (du 1er au dernier jour du mois)
const getCurrentMonthDates = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
  const lastDayStr = String(lastDay).padStart(2, '0');
  return {
    startDate: `${year}-${month}-01`,
    endDate: `${year}-${month}-${lastDayStr}`
  };
};

const Marge = ({ currentUser }) => {
  const [bills, setBills] = useState([]);
  const [billsPin, setBillsPin] = useState([]); // Factures de l'application pin
  const [products, setProducts] = useState([]);
  const [productsPin, setProductsPin] = useState([]); // Produits de l'application pin
  const [invoices, setInvoices] = useState([]);
  const [technicalSheets, setTechnicalSheets] = useState([]); // Fiches techniques
  const [locations, setLocations] = useState([]);
  const [error, setError] = useState('');
  
  // 📅 Filtres initialisés par défaut sur le mois en cours
  const [filters, setFilters] = useState({
    ...getCurrentMonthDates(),
    location: '',
    search: '',
    family: ''
  });
  const [searchTerm, setSearchTerm] = useState(filters.search);

  // ⏳ Débruitage (Debounce) pour la recherche produit
  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchTerm }));
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);
  
  // États de chargement
  const [loadingBase, setLoadingBase] = useState(false);
  const [loadingBills, setLoadingBills] = useState(false);

  // 🛡️ Helpers de formatage sécurisés contre les plantages
  const formatDate = useCallback((dateVal) => {
    if (!dateVal) return 'N/A';
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('fr-FR');
  }, []);

  const formatAmount = useCallback((val, decimals = 2) => {
    const n = parseFloat(val);
    if (isNaN(n)) return (0).toFixed(decimals);
    return n.toFixed(decimals);
  }, []);

  // 🔍 Fonction sécurisée pour extraire l'ID de localisation
  const extractLocId = useCallback((loc) => {
    if (!loc) return null;
    if (typeof loc === "string") return loc;
    if (loc.$oid) return loc.$oid;
    if (loc._id) {
      if (typeof loc._id === "string") return loc._id;
      if (loc._id.$oid) return loc._id.$oid;
      return loc._id.toString();
    }
    return loc.toString ? loc.toString() : null;
  }, []);

  // 🗺️ Map O(1) pour l'accès instantané aux noms de localisations
  const locationMap = React.useMemo(() => {
    const map = new Map();
    (locations || []).forEach(loc => {
      const id = extractLocId(loc._id || loc);
      if (id) map.set(id, loc.name);
    });
    return map;
  }, [locations, extractLocId]);

  // 📌 Helper pour déterminer l'ID de localisation d'une facture de vente
  const getBillLocationId = useCallback((bill) => {
    if (!bill) return 'unknown';
    let locId = extractLocId(bill.localisation);
    if (!locId && bill.user) {
      locId = extractLocId(bill.user.localisation);
    }
    return locId || 'unknown';
  }, [extractLocId]);

  // 🎯 Helper de correspondance exacte pour le filtrage par localisation (par ID ou par Nom)
  const isLocationMatch = useCallback((filterLocId, bill, detailsLocId) => {
    if (!filterLocId) return true; // Aucun filtre de localisation sélectionné

    const selectedLocObj = locations.find(l => extractLocId(l._id) === filterLocId);
    const selectedLocName = (selectedLocObj?.name || "").toLowerCase().trim();

    const bLocId = getBillLocationId(bill);
    const iLocId = extractLocId(detailsLocId);

    if (bLocId === filterLocId || iLocId === filterLocId) {
      return true;
    }

    if (selectedLocName) {
      const bLocStr = (typeof bill.localisation === 'string' ? bill.localisation : bill.localisation?.name || "").toLowerCase().trim();
      const uLocStr = (bill.user?.localisation ? String(bill.user.localisation) : "").toLowerCase().trim();
      if (bLocStr.includes(selectedLocName) || uLocStr.includes(selectedLocName) || selectedLocName.includes(bLocStr)) {
        return true;
      }
    }

    return false;
  }, [locations, extractLocId, getBillLocationId]);

  // 📊 Charger les données de base (Produits, factures d'achat, etc.) UNE SEULE FOIS
  const fetchBaseData = useCallback(async () => {
    setLoadingBase(true);
    setError('');
    try {
      const token = localStorage.getItem("token");
      const [
        productsRes, 
        invoicesRes, 
        locationsRes,
        productsPinRes,
        sheetsRes
      ] = await Promise.all([
        axios.get("/api/ProductV/getProductV?slim=true", { headers: { Authorised: token } }).catch(e => { console.error("Error ProductV:", e.response?.data || e); return { data: [] }; }),
        axios.get("/api/InvoicesV/getInvoices?slim=true").catch(e => { console.error("Error InvoicesV:", e.response?.data || e); return { data: [] }; }),
        axios.get("/api/Locations/getAllLocations").catch(e => { console.error("Error Locations:", e.response?.data || e); return { data: [] }; }),
        axios.get("/api/Product/getProduct?slim=true", { headers: { Authorised: token } }).catch(e => { console.error("Error Product App2:", e.response?.data || e); return { data: [] }; }),
        axios.get("/api/TechnicalSheet/all-sheets?slim=true", { headers: { Authorised: token } }).catch(e => { console.error("Error TechSheets:", e.response?.data || e); return { data: [] }; })
      ]);

      setProducts(productsRes.data || []);
      setInvoices(invoicesRes.data || []);
      const filteredLocs = (locationsRes.data || []).filter(loc => {
        const nameLower = (loc.name || "").toLowerCase();
        return !nameLower.includes("family");
      });
      setLocations(filteredLocs);
      setProductsPin(productsPinRes.data || []);
      setTechnicalSheets(sheetsRes.data || []);

      console.log("📦 Données de base chargées avec succès.");
    } catch (err) {
      console.error("❌ Erreur chargement base:", err);
      setError('Erreur lors du chargement des données de base');
    } finally {
      setLoadingBase(false);
    }
  }, []);

  // 📊 Charger les factures de vente selon la période
  const fetchBills = useCallback(async (start, end) => {
    setLoadingBills(true);
    try {
      console.time("FetchBills");
      const token = localStorage.getItem("token");
      
      let queryParams = "?slim=true";
      if (start) queryParams += `&startDate=${start}`;
      if (end) queryParams += `&endDate=${end}`;

      const [billsRes, billsPinRes] = await Promise.all([
        axios.get(`/api/BillsValises/getAllBillsV${queryParams}`, { headers: { Authorised: token } }).catch(e => { console.error("Error BillsV:", e); return { data: [] }; }),
        axios.get(`/api/Bills/getAllBills${queryParams}`, { headers: { Authorised: token } }).catch(e => { console.error("Error Bills Pin:", e); return { data: [] }; })
      ]);

      setBills(billsRes.data || []);
      setBillsPin(billsPinRes.data || []);
      console.timeEnd("FetchBills");
    } catch (err) {
      console.error("❌ Erreur chargement factures:", err);
      setError("Erreur lors du chargement des factures de vente");
    } finally {
      setLoadingBills(false);
    }
  }, []);

  useEffect(() => {
    fetchBaseData();
  }, [fetchBaseData]);

  useEffect(() => {
    fetchBills(filters.startDate, filters.endDate);
  }, [filters.startDate, filters.endDate, fetchBills]);

  // 🔥 OPTIMISATION: Pré-calculer les prix d'achat dans une Map (O(1) au lieu de O(N))
  const purchasePriceMap = React.useMemo(() => {
    const map = new Map();
    for (const invoice of invoices) {
      if (!invoice.items || !Array.isArray(invoice.items)) continue;
      for (const item of invoice.items) {
        let purchasePriceTTC;
        if (item.pricettc && item.pricettc > 0) {
          purchasePriceTTC = item.pricettc;
        } else {
          const purchasePriceHT = item.price || 0;
          const tvaRate = item.tva || 0;
          purchasePriceTTC = purchasePriceHT * (1 + tvaRate / 100);
        }

        const itemProductId = extractLocId(item.productId || item.productV || item.productV?._id);
        const itemSubProductId = extractLocId(item.subProductId || item.subProductV || item.subProductV?._id);

        if (itemSubProductId) {
          map.set(`sub_${itemSubProductId}`, purchasePriceTTC);
        }
        if (itemProductId && !map.has(`prod_${itemProductId}`)) {
          map.set(`prod_${itemProductId}`, purchasePriceTTC);
        }
      }
    }
    return map;
  }, [invoices, extractLocId]);

  // 🔥 OPTIMISATION: Pré-calculer les produits App 1 dans une Map
  const productMapApp1 = React.useMemo(() => {
    const map = new Map();
    for (const product of products) {
      const pId = extractLocId(product._id);
      if (pId) {
        map.set(`prod_${pId}`, { ...product, isSub: false });
        if (product.subProductsV && Array.isArray(product.subProductsV)) {
          for (const sub of product.subProductsV) {
            const sId = extractLocId(sub._id);
            if (sId) {
              map.set(`sub_${sId}`, { 
                ...sub, 
                parentName: product.name, 
                parentId: product._id, 
                parentCategory: product.category,
                isSub: true 
              });
            }
          }
        }
      }
    }
    return map;
  }, [products, extractLocId]);

  const findPurchasePrice = useCallback((productId, subProductId) => {
    if (subProductId) {
      const sId = extractLocId(subProductId);
      if (sId && purchasePriceMap.has(`sub_${sId}`)) return purchasePriceMap.get(`sub_${sId}`);
    }
    if (productId) {
      const pId = extractLocId(productId);
      if (pId && purchasePriceMap.has(`prod_${pId}`)) return purchasePriceMap.get(`prod_${pId}`);
    }
    return 0;
  }, [purchasePriceMap, extractLocId]);

  // 🔍 Obtenir les détails d'un produit avec le bon prix d'achat TTC (Application 1)
  const getProductDetailsApp1 = useCallback((item) => {
    const itemId = extractLocId(item.id || item._id);
    const prodMatch = productMapApp1.get(`prod_${itemId}`);
    const subMatch = productMapApp1.get(`sub_${itemId}`);

    if (prodMatch && !prodMatch.isSub) {
      let purchasePriceTTC = 0;
      if (prodMatch.category === "SET" && prodMatch.subProductsV?.length > 0) {
        purchasePriceTTC = prodMatch.subProductsV.reduce((total, subProduct) => {
          return total + findPurchasePrice(prodMatch._id, subProduct._id);
        }, 0);
      } else {
        purchasePriceTTC = findPurchasePrice(prodMatch._id, null);
      }
      return {
        _id: prodMatch._id,
        parent: prodMatch.name,
        name: null,
        sellingPrice: item.price !== undefined ? item.price : (prodMatch.price || 0),
        purchasePrice: purchasePriceTTC,
        localisation: extractLocId(prodMatch.localisation),
        hasSubProduct: false,
        category: prodMatch.category || "Autre",
        isSet: prodMatch.category === "SET",
        type: 'achat'
      };
    }

    if (subMatch && subMatch.isSub) {
      const purchasePriceTTC = findPurchasePrice(subMatch.parentId, subMatch._id);
      return {
        _id: subMatch._id,
        parent: subMatch.parentName,
        name: subMatch.name,
        sellingPrice: item.price !== undefined ? item.price : (subMatch.price || 0),
        purchasePrice: purchasePriceTTC,
        localisation: extractLocId(subMatch.localisation),
        hasSubProduct: true,
        category: subMatch.parentCategory || "Autre",
        isSet: false,
        type: 'achat'
      };
    }

    return {
      _id: item.id || item._id,
      name: item.name || "Produit inconnu",
      parent: item.name || "Produit inconnu",
      sellingPrice: item.price || 0,
      purchasePrice: 0,
      localisation: null,
      hasSubProduct: false,
      category: "Autre",
      isSet: false,
      type: 'achat'
    };
  }, [productMapApp1, findPurchasePrice, extractLocId]);

  // ==================== LOGIQUE APPLICATION 2 (PRODUCTION) ====================

  // 🔥 OPTIMISATION: Pré-calculer les produits App 2 dans une Map
  const productMapApp2 = React.useMemo(() => {
    const map = new Map();
    for (const product of productsPin) {
      const pId = extractLocId(product._id);
      if (pId) {
        map.set(`prod_${pId}`, { ...product, isSub: false });
        if (product.subProducts && Array.isArray(product.subProducts)) {
          for (const sub of product.subProducts) {
            const sId = extractLocId(sub._id);
            if (sId) {
              map.set(`sub_${sId}`, { 
                ...sub, 
                parentName: product.name, 
                parentId: product._id, 
                parentCategory: product.category,
                isSub: true 
              });
            }
          }
        }
      }
    }
    return map;
  }, [productsPin, extractLocId]);

  // 🔥 OPTIMISATION: Pré-calculer les fiches techniques dans une Map
  const technicalSheetMap = React.useMemo(() => {
    const map = new Map();
    for (const sheet of technicalSheets) {
      if (sheet.subProduct) map.set(extractLocId(sheet.subProduct), sheet.totalCost);
      if (sheet.product) map.set(extractLocId(sheet.product), sheet.totalCost);
    }
    return map;
  }, [technicalSheets, extractLocId]);

  // 🔍 Obtenir les détails d'un produit (Application 2)
  const getProductDetailsApp2 = useCallback((item) => {
    const itemId = extractLocId(item.id || item._id);
    const prodMatch = productMapApp2.get(`prod_${itemId}`);
    const subMatch = productMapApp2.get(`sub_${itemId}`);

    let productName = item.name || "Produit inconnu";
    let localisation = null;
    let defaultCost = 0;

    if (subMatch && subMatch.isSub) {
      productName = subMatch.parentName;
      localisation = extractLocId(subMatch.localisation);
      defaultCost = (subMatch.price || 0) * 0.7;
    } else if (prodMatch && !prodMatch.isSub) {
      productName = prodMatch.name;
      localisation = extractLocId(prodMatch.localisation);
    }

    const sheetCost = technicalSheetMap.get(itemId);
    const productionCost = sheetCost !== undefined ? sheetCost : defaultCost;

    let category = "Autre";
    if (subMatch && subMatch.isSub) category = subMatch.parentCategory || "Autre";
    else if (prodMatch && !prodMatch.isSub) category = prodMatch.category || "Autre";

    return {
      _id: item.id || item._id,
      parent: productName,
      name: subMatch ? subMatch.name : null,
      sellingPrice: item.price || 0,
      purchasePrice: productionCost,
      localisation: localisation,
      hasSubProduct: false,
      category: category,
      isSet: false,
      type: 'production'
    };
  }, [productMapApp2, technicalSheetMap, extractLocId]);

  // 📈 Calculer les marges avec les bons prix (COMBINÉ)
  const calculateMargins = useCallback(() => {
    const marginsByLocation = {};
    let totalMargin = 0;
    let totalSales = 0;
    let totalCost = 0;

    console.time("ProcessingMargins");

    // Traiter chaque facture de vente (Application 1)
    (bills || []).forEach(bill => {
      if (bill.status === "cancelled" || !bill.items || !Array.isArray(bill.items)) return;

      const billLocId = getBillLocationId(bill);

      bill.items.forEach(item => {
        if (item.isCancelled) return;
        const details = getProductDetailsApp1(item);
        const quantity = item.quantity || 1;
        
        // Vérifier le filtre de famille (catégorie)
        if (filters.family && details.category?.toLowerCase() !== filters.family?.toLowerCase()) {
          return;
        }

        // Vérifier le filtre de localisation (match par ID ou par Nom)
        if (!isLocationMatch(filters.location, bill, details.localisation)) {
          return;
        }

        // Vérifier le filtre de recherche (nom ou sous-produit)
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const productName = (details.parent || "").toLowerCase();
          const subProductName = (details.name || "").toLowerCase();
          if (!productName.includes(searchLower) && !subProductName.includes(searchLower)) {
            return;
          }
        }

        // Calculs basés sur les prix TTC
        const sellingPrice = details.sellingPrice || 0;
        const purchasePrice = details.purchasePrice || 0;
        const salesAmount = sellingPrice * quantity;
        const costAmount = purchasePrice * quantity;
        const margin = salesAmount - costAmount;
        const marginPercentage = salesAmount > 0 ? (margin / salesAmount) * 100 : 0;

        // Regrouper par localisation de la vente (prioritaire) ou du produit
        const itemLocId = extractLocId(details.localisation);
        const locationId = (billLocId !== 'unknown' ? billLocId : itemLocId) || 'unknown';
        const locationName = locationMap.get(locationId) || 'Localisation inconnue';

        if (!marginsByLocation[locationId]) {
          marginsByLocation[locationId] = {
            name: locationName,
            sales: 0,
            cost: 0,
            margin: 0,
            percentage: 0,
            items: []
          };
        }

        marginsByLocation[locationId].sales += salesAmount;
        marginsByLocation[locationId].cost += costAmount;
        marginsByLocation[locationId].margin += margin;
        
        // Ajouter l'item détaillé
        marginsByLocation[locationId].items.push({
          date: bill.date || bill.createdAt,
          product: details.parent || details.name || "Produit inconnu",
          subProduct: details.name,
          quantity,
          sellingPrice,
          purchasePrice,
          salesAmount,
          costAmount,
          margin,
          marginPercentage,
          type: details.type,
          tableNumber: bill.tableNumber || 'N/A'
        });

        // Totaux généraux
        totalSales += salesAmount;
        totalCost += costAmount;
        totalMargin += margin;
      });
    });

    // Traiter chaque facture de vente (Application 2)
    (billsPin || []).forEach(bill => {
      if (bill.isStaffAddition || !bill.items || !Array.isArray(bill.items)) return;

      const billLocId = getBillLocationId(bill);

      bill.items.forEach(item => {
        if (item.isCancelled) return;
        const details = getProductDetailsApp2(item);
        const quantity = item.quantity || 1;
        
        // Vérifier le filtre de famille
        if (filters.family && details.category?.toLowerCase() !== filters.family?.toLowerCase()) {
          return;
        }
        
        // Vérifier le filtre de localisation
        if (!isLocationMatch(filters.location, bill, details.localisation)) {
          return;
        }

        // Vérifier le filtre de recherche (nom ou sous-produit)
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const productName = (details.parent || "").toLowerCase();
          const subProductName = (details.name || "").toLowerCase();
          if (!productName.includes(searchLower) && !subProductName.includes(searchLower)) {
            return;
          }
        }

        // Calculs basés sur les coûts de production
        const sellingPrice = details.sellingPrice || 0;
        const purchasePrice = details.purchasePrice || 0;
        const salesAmount = sellingPrice * quantity;
        const costAmount = purchasePrice * quantity;
        const margin = salesAmount - costAmount;
        const marginPercentage = salesAmount > 0 ? (margin / salesAmount) * 100 : 0;

        // Regrouper par localisation
        const itemLocId = extractLocId(details.localisation);
        const locationId = (billLocId !== 'unknown' ? billLocId : itemLocId) || 'unknown';
        const locationName = locationMap.get(locationId) || 'Localisation inconnue';

        if (!marginsByLocation[locationId]) {
          marginsByLocation[locationId] = {
            name: locationName,
            sales: 0,
            cost: 0,
            margin: 0,
            percentage: 0,
            items: []
          };
        }

        marginsByLocation[locationId].sales += salesAmount;
        marginsByLocation[locationId].cost += costAmount;
        marginsByLocation[locationId].margin += margin;
        
        // Ajouter l'item détaillé
        marginsByLocation[locationId].items.push({
          date: bill.date || bill.createdAt,
          product: details.parent || details.name || "Produit inconnu",
          subProduct: details.name,
          quantity,
          sellingPrice,
          purchasePrice,
          salesAmount,
          costAmount,
          margin,
          marginPercentage,
          type: details.type,
          tableNumber: bill.tableNumber || 'N/A'
        });

        // Totaux généraux
        totalSales += salesAmount;
        totalCost += costAmount;
        totalMargin += margin;
      });
    });

    // Calculer les pourcentages par localisation
    Object.keys(marginsByLocation).forEach(locId => {
      const locData = marginsByLocation[locId];
      locData.percentage = locData.sales > 0 ? (locData.margin / locData.sales) * 100 : 0;
    });

    console.timeEnd("ProcessingMargins");

    return {
      byLocation: marginsByLocation,
      totals: {
        sales: totalSales,
        cost: totalCost,
        margin: totalMargin,
        percentage: totalSales > 0 ? (totalMargin / totalSales) * 100 : 0
      }
    };
  }, [
    bills, 
    billsPin, 
    filters, 
    getProductDetailsApp1, 
    getProductDetailsApp2, 
    getBillLocationId,
    isLocationMatch,
    locationMap,
    extractLocId
  ]);

  const marginsData = React.useMemo(() => calculateMargins(), [calculateMargins]);

  // 🔄 Réinitialiser les filtres au mois en cours
  const resetFilters = () => {
    const defaultDates = getCurrentMonthDates();
    setFilters({
      ...defaultDates,
      location: '',
      search: '',
      family: ''
    });
    setSearchTerm('');
  };

  // 📥 Exporter les données filtrées en Excel (Résumé + Détails)
  const exportToExcel = () => {
    try {
      const excelData = [];
      
      // Parcourir chaque localisation et ses éléments filtrés
      Object.entries(marginsData.byLocation).forEach(([locId, data]) => {
        data.items.forEach(item => {
          excelData.push({
            'Date': formatDate(item.date),
            'Localisation': data.name,
            'Table': item.tableNumber,
            'Type': item.type === 'achat' ? 'Achat' : 'Production',
            'Produit': item.product,
            'Sous-produit': item.subProduct || '-',
            'Quantité': item.quantity,
            'Prix Vente (DT)': item.sellingPrice,
            'Coût (DT)': item.purchasePrice,
            'Chiffre d\'Affaires (DT)': item.salesAmount,
            'Coût Total (DT)': item.costAmount,
            'Marge (DT)': item.margin,
            'Marge (%)': parseFloat(item.marginPercentage.toFixed(1))
          });
        });
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Marges Détaillées");

      // Deuxième feuille : Résumé par localisation
      const summaryData = Object.entries(marginsData.byLocation).map(([locId, data]) => ({
        'Localisation': data.name,
        'Chiffre d\'Affaires (DT)': data.sales,
        'Coût Total (DT)': data.cost,
        'Marge (DT)': data.margin,
        'Pourcentage Marge (%)': parseFloat(data.percentage.toFixed(1))
      }));

      // Ajouter le total général au résumé
      summaryData.push({
        'Localisation': 'TOTAL GÉNÉRAL',
        'Chiffre d\'Affaires (DT)': marginsData.totals.sales,
        'Coût Total (DT)': marginsData.totals.cost,
        'Marge (DT)': marginsData.totals.margin,
        'Pourcentage Marge (%)': parseFloat(marginsData.totals.percentage.toFixed(1))
      });

      const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "Résumé par Localisation");

      XLSX.writeFile(workbook, `rapport_marges_${filters.startDate}_a_${filters.endDate}.xlsx`);
    } catch (err) {
      console.error("❌ Erreur lors de l'export Excel:", err);
      alert("Une erreur est survenue lors de la création du fichier Excel.");
    }
  };

  // 📊 Statistiques résumées
  const getStatsSummary = () => {
    const locationsCount = Object.keys(marginsData.byLocation).length;
    const totalItems = Object.values(marginsData.byLocation).reduce(
      (sum, loc) => sum + loc.items.length, 0
    );

    // Calculer les totaux par type
    const totalsByType = { achat: 0, production: 0 };
    Object.values(marginsData.byLocation).forEach(loc => {
      loc.items.forEach(item => {
        if (item.type === 'achat') {
          totalsByType.achat++;
        } else if (item.type === 'production') {
          totalsByType.production++;
        }
      });
    });

    return {
      locationsCount,
      totalItems,
      totalsByType,
      ...marginsData.totals
    };
  };

  const stats = getStatsSummary();

  // Obtenir la liste dynamique des familles
  const availableFamilies = React.useMemo(() => {
    const families = new Set();
    for (const prod of products || []) {
      if (prod.category) families.add(prod.category);
    }
    for (const prod of productsPin || []) {
      if (prod.category) families.add(prod.category);
    }
    return Array.from(families).sort();
  }, [products, productsPin]);

  if (loadingBase) {
    return (
      <div className="text-center mt-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </Spinner>
        <p className="mt-2">Chargement des données de base...</p>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h2 className="mb-4 d-flex align-items-center justify-content-between">
        <span>📈 État des Marges (Combiné)</span>
        {loadingBills && (
          <Spinner animation="border" size="sm" className="text-primary" role="status">
            <span className="visually-hidden">Chargement des factures...</span>
          </Spinner>
        )}
      </h2>
      
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Filtres */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Filtres</h5>
          <Button variant="outline-secondary" size="sm" onClick={resetFilters}>
            🔄 Réinitialiser au mois en cours
          </Button>
        </Card.Header>
        <Card.Body>
          <Row className="align-items-end">
            <Col md={2}>
              <Form.Group>
                <Form.Label>Date de début</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Date de fin</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Famille (Catégorie)</Form.Label>
                <Form.Select
                  value={filters.family}
                  onChange={(e) => setFilters({...filters, family: e.target.value})}
                >
                  <option value="">Toutes les familles</option>
                  {availableFamilies.map(fam => (
                    <option key={fam} value={fam}>{fam}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Localisation</Form.Label>
                <Form.Select
                  value={filters.location}
                  onChange={(e) => setFilters({...filters, location: e.target.value})}
                >
                  <option value="">Toutes</option>
                  {locations.map(loc => (
                    <option key={extractLocId(loc._id)} value={extractLocId(loc._id)}>
                      {loc.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Recherche Produit</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Nom du produit..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col className="text-end">
              <button 
                type="button" 
                className="btn btn-success" 
                onClick={exportToExcel}
                disabled={Object.keys(marginsData.byLocation).length === 0}
              >
                📊 Exporter en Excel
              </button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Contenu principal */}
      <div style={{ 
        opacity: loadingBills ? 0.6 : 1, 
        transition: 'opacity 0.15s ease-in-out',
        pointerEvents: loadingBills ? 'none' : 'auto'
      }}>
        {/* Résumé général */}
        <Card className="mb-4 bg-light">
          <Card.Body>
            <Row>
              <Col md={3} className="text-center">
                <h6>Chiffre d'Affaires</h6>
                <h4 className="text-primary">{formatAmount(stats.sales)} DT</h4>
                <small className="text-muted">
                  📦 Achat: {stats.totalsByType.achat} ventes
                </small>
                <br />
                <small className="text-muted">
                  🏭 Production: {stats.totalsByType.production} ventes
                </small>
              </Col>
              <Col md={3} className="text-center">
                <h6>Coût Total</h6>
                <h4 className="text-danger">{formatAmount(stats.cost)} DT</h4>
              </Col>
              <Col md={3} className="text-center">
                <h6>Marge Totale</h6>
                <h4 className={stats.margin >= 0 ? "text-success" : "text-danger"}>
                  {formatAmount(stats.margin)} DT
                </h4>
              </Col>
              <Col md={3} className="text-center">
                <h6>Pourcentage Marge</h6>
                <h4 className={stats.percentage >= 0 ? "text-success" : "text-danger"}>
                  {formatAmount(stats.percentage, 1)} %
                </h4>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col className="text-center">
                <small className="text-muted">
                  {stats.locationsCount} localisation(s) • {stats.totalItems} ventes totales
                </small>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Détails par localisation */}
        {Object.entries(marginsData.byLocation).map(([locId, data]) => (
          <Card key={locId} className="mb-4">
            <Card.Header className="bg-secondary text-white">
              <h5 className="mb-0">
                📍 {data.name}
                <span className="float-end">
                  Marge: {formatAmount(data.margin)} DT ({formatAmount(data.percentage, 1)}%)
                </span>
              </h5>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover size="sm" responsive>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Table</th>
                    <th>Type</th>
                    <th>Produit</th>
                    <th>Sous-produit</th>
                    <th>Quantité</th>
                    <th>Prix Vente</th>
                    <th>Coût</th>
                    <th>CA</th>
                    <th>Coût Total</th>
                    <th>Marge</th>
                    <th>%</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item, index) => (
                    <tr key={index}>
                      <td>{formatDate(item.date)}</td>
                      <td>{item.tableNumber}</td>
                      <td>
                        <span className={`badge ${item.type === 'achat' ? 'bg-info' : 'bg-warning'}`}>
                          {item.type === 'achat' ? '📦 Achat' : '🏭 Production'}
                        </span>
                      </td>
                      <td>{item.product}</td>
                      <td>{item.subProduct || '-'}</td>
                      <td>{item.quantity}</td>
                      <td>{formatAmount(item.sellingPrice)} DT</td>
                      <td>{formatAmount(item.purchasePrice)} DT</td>
                      <td>{formatAmount(item.salesAmount)} DT</td>
                      <td>{formatAmount(item.costAmount)} DT</td>
                      <td className={item.margin >= 0 ? "text-success" : "text-danger"}>
                        {formatAmount(item.margin)} DT
                      </td>
                      <td className={item.marginPercentage >= 0 ? "text-success" : "text-danger"}>
                        {formatAmount(item.marginPercentage, 1)}%
                      </td>
                    </tr>
                  ))}
                  {/* Total de la localisation */}
                  <tr className="table-info fw-bold">
                    <td colSpan="8">Total {data.name}</td>
                    <td>{formatAmount(data.sales)} DT</td>
                    <td>{formatAmount(data.cost)} DT</td>
                    <td className={data.margin >= 0 ? "text-success" : "text-danger"}>
                      {formatAmount(data.margin)} DT
                    </td>
                    <td className={data.percentage >= 0 ? "text-success" : "text-danger"}>
                      {formatAmount(data.percentage, 1)}%
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        ))}

        {Object.keys(marginsData.byLocation).length === 0 && !loadingBase && !loadingBills && (
          <Alert variant="info" className="text-center">
            Aucune donnée de marge trouvée pour les critères sélectionnés.
          </Alert>
        )}
      </div>
    </div>
  );
};

export default Marge;